import { ChangeEvent, CSSProperties, FocusEventHandler, forwardRef, InputHTMLAttributes, ReactNode, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

import clsx from 'clsx';

import styles from './inputField.module.css';

import { useInputField } from '../../hooks/useInputField';
import { HelpText } from '../HelpText/HelpText';
import { InvalidMessage } from '../InvalidMessage/InvalidMessage';

type InputProps = InputHTMLAttributes<HTMLInputElement>;

export type InputBoxHeight = 'tiny' | 'compact' | 'default';

export type InputFieldProps = InputProps & {
	/**
	 * Adds a label to the input field. This is required for accessibilty.
	 */
	label?: string;

	/**
	 * Include label, but keep it visually hidden (for accessibility)
	 */
	hideLabel?: boolean;

	/**
	 * Add an additional help text below the input field.
	 */
	helpText?: string;

	/**
	 * Prefix value with custom text
	 */
	prefix?: string;

	/**
	 * Add an additional help text below the input field.
	 */
	invalidMessage?: string;

	/**
	 * Set styling to indicate input is invalid.
	 * Also shows the `invalidMessage` if provided
	 */
	isInvalid?: boolean;

	/**
	 * When active the label will move up above the actual input field
	 */
	isActive?: boolean;

	/**
	 * When provided this will overrule internal focus state
	 */
	inFocus?: boolean;

	/**
	 * Will set height of input field
	 *
	 * `'default'`: 58px
	 *
	 * `'compact'`: 50px (same height as buttons)
	 *
	 */
	height?: InputBoxHeight;

	/**
	 * Append a string or a component to the input field
	 */
	append?: ReactNode;

	/**
	 * Like append, but in the beginning
	 */
	prepend?: ReactNode;

	/**
	 * Take over rendering of the input field to fully customize (actual input field will be placed
	 * visually hidden above this)
	 */
	renderInput?: (value: string) => ReactNode;

	/**
	 * If it's a Selector
	 */
	isSelector?: boolean;

	/**
	 * Custom inline styles for the wrapper element
	 */
	style?: CSSProperties;

	withDisabledStyling?: boolean;
};

export const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
	(
		{
			label,
			hideLabel,
			helpText,
			prefix,
			invalidMessage,
			id,
			children,
			isActive,
			inFocus,
			isInvalid,
			height = 'default',
			append,
			prepend,
			renderInput,
			disabled,
			placeholder,
			onClick,
			onClickCapture,
			type,
			onBlur,
			className,
			withDisabledStyling,
			isSelector,
			onChange,
			value = '',
			style,

			...rest
		},
		ref,
	) => {
		const inputRef = useRef<HTMLInputElement | null>(null);

		const [isValidityValid, setIsValidityValid] = useState(true);
		const [hasFocus, setHasFocus] = useState(false);
		const [_value, setValue] = useState<string | undefined>(typeof value === 'string' ? value : undefined);

		const { fieldId, helpTextId, invalidMessageId, describedById, showHelpText, showInvalidMessage } = useInputField({
			id,
			helpText,
			isInvalid,
			invalidMessage,
		});

		useEffect(() => {
			if (inputRef.current) {
				if (typeof value === 'string' && value.length) {
					inputRef.current.value = value;
					setValue(value);
				} else if (value === undefined || value === '') {
					inputRef.current.value = '';
					setValue(undefined);
				}
			}
		}, [value, inputRef]);

		const { defaultValue } = rest;

		const pristine = typeof _value === 'undefined';
		const hasValue = !!_value || !!(pristine && defaultValue);

		const isInputActive = !!isActive || hasFocus || hasValue || !!inFocus;

		const isValid = !isInvalid && !showInvalidMessage && isValidityValid;

		const onFocusHandler = () => setHasFocus(true);
		const onBlurHandler: FocusEventHandler<HTMLInputElement> = (e) => {
			setHasFocus(false);
			setIsValidityValid(inputRef.current?.validity.valid ? true : false);
			onBlur?.(e);
		};

		const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
			setValue(e.target.value);
			onChange?.(e);
		};

		useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, [ref]);

		const inputRefCallback = useCallback((node: HTMLInputElement | null) => {
			if (node !== null) {
				inputRef.current = node;
				setValue(node.value);
			}
		}, []);

		const isTextType = ['text', 'password', 'email', 'number', 'tel', 'url', 'search'].includes(type || 'text');
		const focusState = hasFocus || inFocus;

		return (
			<div
				data-input-root
				className={clsx(styles.root, className)}
				style={style}
			>
				<div
					data-input-box
					key={fieldId}
					className={clsx(styles.box, {
						[styles.compact]: height === 'compact',
						[styles.tiny]: height === 'tiny',
						[styles.invalid]: !isValid,
						[styles.active]: isInputActive,
						[styles.hasFocus]: focusState,
						[styles.inactive]: disabled || withDisabledStyling,
						[styles.typeText]: isTextType,
					})}
					onClick={onClick}
					onClickCapture={onClickCapture}
				>
					<label
						className={styles.inputLabel}
						htmlFor={fieldId}
						aria-label={hideLabel ? label : undefined}
					>
						{label && !hideLabel ? (
							<div
								className={clsx(styles.labelContent, {
									[styles.hasFocus]: focusState,
									[styles.active]: isInputActive,
									[styles.invalid]: !isValid,
									[styles.inactive]: disabled || withDisabledStyling,
								})}
							>
								<span className={styles.labelText}>{label}</span>
							</div>
						) : null}
					</label>

					{prepend ? (
						<span
							className={clsx(styles.additionalContent, {
								[styles.hasFocus]: focusState,
								[styles.active]: isInputActive,
								[styles.invalid]: !isValid,
							})}
						>
							{prepend}
						</span>
					) : null}

					{prefix ? <span className={clsx(styles.prefix, { [styles.hide]: !!label && !hideLabel && !isInputActive })}>{prefix}&nbsp;</span> : null}

					<div className={clsx(styles.inputWrap, { [styles.isSelector]: isSelector })}>
						{_value && renderInput ? renderInput(_value) : null}
						<input
							data-input
							className={clsx(styles.input, {
								[styles.visuallyHidden]: !!_value && !!renderInput,
								[styles.withLabel]: !!label && !hideLabel,
								[styles.withPrefix]: !!prefix,
								[styles.withAppend]: !!append,
								[styles.active]: isInputActive,
								[styles.disabled]: disabled,
								[styles.withDisabledStyling]: withDisabledStyling,
							})}
							id={fieldId}
							aria-describedby={describedById}
							ref={inputRefCallback}
							onFocus={onFocusHandler}
							onBlur={onBlurHandler}
							disabled={disabled}
							placeholder={placeholder}
							type={type}
							onChange={handleChange}
							{...rest}
						/>
					</div>

					{children}

					{append ? (
						<span
							className={clsx(styles.additionalContent, {
								[styles.hasFocus]: focusState,
								[styles.active]: isInputActive,
								[styles.invalid]: !isValid,
							})}
						>
							{append}
						</span>
					) : null}
				</div>
				{showInvalidMessage ? (
					<InvalidMessage
						id={invalidMessageId}
						children={invalidMessage}
					/>
				) : null}
				{showHelpText ? (
					<HelpText
						id={helpTextId}
						children={helpText}
					/>
				) : null}
			</div>
		);
	},
);
